/**
 * DebugLobby — debug-lobby.
 *
 * Tight focused map for QA + debugging sessions. Auto-enables every
 * dev/diagnostic overlay (LatencyOverlay, NetobsPacketSparkline,
 * NetTrafficOverlay, DebugInfoOverlay, multiplayer diagnostics) via
 * the `__debugLobbyAutoEnable` hook in main.tsx — no URL flags needed.
 *
 * Layout: small arena — 60×30 tiles, two raised platforms for jump
 * testing, central spawn, ground plate. Borrowed sizing from
 * AbilityRangeTest but stripped down to "can I see things" rather
 * than "can I land an ability."
 *
 *     x=0 ──── x=59
 *   y=0 ┌───────────┐
 *       │           │
 *       │ ▣▣ ·  ▣▣  │  ← side platforms (4t each)
 *       │           │
 *       │     ·     │  ← spawn (30, 22)
 *  y=24 ├───────────┤  ← ground surface
 *       │░░░░░░░░░░░│  ← ground mass (5t deep)
 *  y=29 └░░░░░░░░░░░┘
 *
 * Use: bug repro, network telemetry inspection, quick visual sanity.
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';

const MAP_ID = 'debug-lobby';
const MAP_NAME = 'Debug Lobby';
const VERSION = '1.0';

const T = 32;
const W = 60;
const H = 30;

const GROUND_TOP_ROW = 24;
const GROUND_DEPTH = 5;
const SPAWN_X = 30;
const SPAWN_Y = 22;

const spawnPoints = [{ x: SPAWN_X, y: SPAWN_Y }];
const defaultSpawn = { x: SPAWN_X * T, y: SPAWN_Y * T };

const runtimePlatforms = [
  // Ground mass
  {
    id: 'ground-mass',
    x: 0,
    y: GROUND_TOP_ROW * T,
    width: W * T,
    height: GROUND_DEPTH * T,
    type: 'normal' as const,
    color: '#2a3540',
  },
  // Left jump platform (4 tiles wide, 7 tiles above ground)
  {
    id: 'left-platform',
    x: 8 * T,
    y: 17 * T,
    width: 4 * T,
    height: T,
    type: 'normal' as const,
    color: '#d35400',  // orange — easy to spot
  },
  // Right jump platform (mirror)
  {
    id: 'right-platform',
    x: 48 * T,
    y: 17 * T,
    width: 4 * T,
    height: T,
    type: 'normal' as const,
    color: '#d35400',
  },
];

// Subtle parallax — single dim band so net overlays stay readable

export const DebugLobby: GridLevel & Partial<PlatformerLevel> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,

  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,

  backgroundColor: '#0c0e16',
  gravity: 1000,

  spawnPoints,
  defaultSpawn,


  layers: [
    { name: LayerType.GROUND,    visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.OBJECTS,   visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.ENTITIES,  visible: true, locked: false, opacity: 1.0, tiles: new Map(), entities: [] },
    { name: LayerType.TRIGGERS,  visible: true, locked: false, opacity: 1.0, tiles: new Map() },
  ],

  platforms: runtimePlatforms,
  entities: [],
  spawnRules: [],
  type: 'town',

  metadata: {
    author: 'DJ',
    description:
      'Compact debug arena — auto-enables all diagnostic overlays via main.tsx hook. ' +
      'Use for bug repro + network telemetry inspection.',
    tags: ['lobby', 'debug', 'diagnostics', 'qa'],
    lobbyTools: { enabled: true },
    profile: 'lobby_safe',
    overrides: {
      pvp: { enabled: true, botBehavior: 'attack-all' },
    },
  },
};

export { W, H, T, MAP_ID, MAP_NAME };
